import React, { useState, useEffect } from 'react';
import { createBodyMeasurement } from '../services/bodyMeasurementsService';
import HeightInputModal from './HeightInputModal';
import type { BodyMeasurementFormData, CreateBodyMeasurementRequest } from '../types/bodyMeasurements';

interface BodyMeasurementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
  profile?: { height?: number | null } | null;
  onProfileUpdate?: (updatedProfile: { height?: number | null } | null) => void;
}

export const BodyMeasurementModal: React.FC<BodyMeasurementModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  profile,
  onProfileUpdate
}) => {
  const [loading, setLoading] = useState(false);
  const [bmiMode, setBmiMode] = useState<'auto' | 'manual'>('auto'); // BMI 계산 모드
  const [showHeightModal, setShowHeightModal] = useState(false);
  const [currentProfile, setCurrentProfile] = useState(profile);
  const [formData, setFormData] = useState<BodyMeasurementFormData>({
    measurement_time: new Date().toISOString(),
    weight: '',
    body_fat_percentage: '',
    body_fat_mass: '',
    fat_free_mass: '',
    skeletal_muscle_mass: '',
    bmi: '',
    waist_circumference: '',
    hip_circumference: '',
    device_type: '',
    device_manufacturer: '',
    data_source: 'manual',
    notes: ''
  });

  // 모달 열릴 때 신장정보 체크
  useEffect(() => {
    if (isOpen) {
      setCurrentProfile(profile);
      // 신장정보가 없으면 신장 입력 모달 띄우기
      if (!profile?.height) {
        setShowHeightModal(true);
      }
    }
  }, [isOpen, profile]);

  // BMI 자동 계산
  useEffect(() => {
    if (bmiMode === 'auto' && formData.weight && currentProfile?.height) {
      const weight = parseFloat(formData.weight);
      const height = currentProfile.height / 100; // cm을 m로 변환
      if (weight > 0 && height > 0) {
        const calculatedBmi = weight / (height * height);
        setFormData(prev => ({
          ...prev,
          bmi: calculatedBmi.toFixed(1)
        }));
      }
    }
  }, [formData.weight, bmiMode, currentProfile?.height]);

  // 신장정보 저장 후 콜백
  const handleHeightSuccess = (height: number) => {
    const updatedProfile = { height: height || null };
    setCurrentProfile(updatedProfile);
    onProfileUpdate?.(updatedProfile);
    setShowHeightModal(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev: BodyMeasurementFormData) => ({
      ...prev,
      [name]: value
    }));

    // BMI 수동 입력 시 모드 변경
    if (name === 'bmi' && value !== '') {
      setBmiMode('manual');
    }
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      weight: value
    }));
    // 체중 입력 시 자동 BMI 계산 모드로 전환
    setBmiMode('auto');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      // 데이터 변환
      const payload: CreateBodyMeasurementRequest = {
        measurement_time: formData.measurement_time,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        body_fat_percentage: formData.body_fat_percentage ? parseFloat(formData.body_fat_percentage) : undefined,
        body_fat_mass: formData.body_fat_mass ? parseFloat(formData.body_fat_mass) : undefined,
        fat_free_mass: formData.fat_free_mass ? parseFloat(formData.fat_free_mass) : undefined,
        skeletal_muscle_mass: formData.skeletal_muscle_mass ? parseFloat(formData.skeletal_muscle_mass) : undefined,
        bmi: formData.bmi ? parseFloat(formData.bmi) : undefined,
        waist_circumference: formData.waist_circumference ? parseFloat(formData.waist_circumference) : undefined,
        hip_circumference: formData.hip_circumference ? parseFloat(formData.hip_circumference) : undefined,
        device_type: formData.device_type || undefined,
        device_manufacturer: formData.device_manufacturer || undefined,
        data_source: 'manual',
        notes: formData.notes || undefined
      };

      const response = await createBodyMeasurement(payload);
      
      if (response.success) {
        onSuccess?.(response.message || '신체정보가 등록되었습니다');
        setFormData({
          measurement_time: new Date().toISOString(),
          weight: '',
          body_fat_percentage: '',
          body_fat_mass: '',
          fat_free_mass: '',
          skeletal_muscle_mass: '',
          bmi: '',
          waist_circumference: '',
          hip_circumference: '',
          device_type: '',
          device_manufacturer: '',
          data_source: 'manual',
          notes: ''
        });
        onClose();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '신체정보 등록 실패';
      onError?.(errorMessage);
      console.error('[BodyMeasurementModal] Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[95vh] overflow-y-auto flex flex-col">
        {/* 헤더 */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">신체정보 등록 (직접입력)</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6 flex-1 overflow-y-auto">
          {/* 측정 시간 */}
          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">
              측정 시간 <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              name="measurement_time"
              value={formData.measurement_time.substring(0, 16)}
              onChange={(e) => {
                const dateTime = new Date(e.target.value).toISOString();
                setFormData((prev: BodyMeasurementFormData) => ({ ...prev, measurement_time: dateTime }));
              }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          {/* 체중 관련 */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">📏 체중</h3>
            {currentProfile?.height ? (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex justify-between items-center">
                <p className="text-sm text-blue-700">신장: <span className="font-semibold">{currentProfile.height} cm</span></p>
                <button
                  type="button"
                  onClick={() => setShowHeightModal(true)}
                  className="text-xs px-2 py-1 bg-blue-200 text-blue-700 rounded hover:bg-blue-300 transition-colors font-medium"
                >
                  수정
                </button>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex justify-between items-center">
                <p className="text-sm text-yellow-700">신장 정보가 없습니다. 설정해주세요.</p>
                <button
                  type="button"
                  onClick={() => setShowHeightModal(true)}
                  className="text-xs px-2 py-1 bg-yellow-200 text-yellow-700 rounded hover:bg-yellow-300 transition-colors font-medium"
                >
                  입력
                </button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  체중 (kg)
                </label>
                <input
                  type="number"
                  name="weight"
                  step="0.1"
                  value={formData.weight}
                  onChange={handleWeightChange}
                  placeholder="예: 70.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BMI {bmiMode === 'auto' ? '(자동 계산)' : '(수동 입력)'}
                </label>
                <input
                  type="number"
                  name="bmi"
                  step="0.1"
                  value={formData.bmi}
                  onChange={handleChange}
                  placeholder="예: 23.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* 신체 구성 */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">🧬 신체 구성</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  체지방률 (%)
                </label>
                <input
                  type="number"
                  name="body_fat_percentage"
                  step="0.1"
                  value={formData.body_fat_percentage}
                  onChange={handleChange}
                  placeholder="예: 25.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  체지방량 (kg)
                </label>
                <input
                  type="number"
                  name="body_fat_mass"
                  step="0.1"
                  value={formData.body_fat_mass}
                  onChange={handleChange}
                  placeholder="예: 18.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  근육량 (kg)
                </label>
                <input
                  type="number"
                  name="skeletal_muscle_mass"
                  step="0.1"
                  value={formData.skeletal_muscle_mass}
                  onChange={handleChange}
                  placeholder="예: 35.2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  무지방량 (kg)
                </label>
                <input
                  type="number"
                  name="fat_free_mass"
                  step="0.1"
                  value={formData.fat_free_mass}
                  onChange={handleChange}
                  placeholder="예: 52.0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* 신체 측정 */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">📐 신체 측정</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  허리둘레 (cm)
                </label>
                <input
                  type="number"
                  name="waist_circumference"
                  step="0.1"
                  value={formData.waist_circumference}
                  onChange={handleChange}
                  placeholder="예: 85.5"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  엉덩이둘레 (cm)
                </label>
                <input
                  type="number"
                  name="hip_circumference"
                  step="0.1"
                  value={formData.hip_circumference}
                  onChange={handleChange}
                  placeholder="예: 95.0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* 기기 정보 (선택사항) */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">📱 기기 정보 (선택사항)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  기기 종류
                </label>
                <input
                  type="text"
                  name="device_type"
                  value={formData.device_type}
                  onChange={handleChange}
                  placeholder="예: Samsung Galaxy Watch"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  제조사
                </label>
                <input
                  type="text"
                  name="device_manufacturer"
                  value={formData.device_manufacturer}
                  onChange={handleChange}
                  placeholder="예: Samsung"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* 메모 */}
          <div className="border-t pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              메모 (선택사항)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="측정 시 특이사항이나 메모를 작성해주세요"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
              rows={3}
            />
          </div>

          {/* 버튼 */}
          <div className="flex gap-3 pt-6 border-t sticky bottom-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '등록 중...' : '등록하기'}
            </button>
          </div>
        </form>
      </div>

      {/* 신장 정보 입력 모달 */}
      <HeightInputModal
        isOpen={showHeightModal}
        onClose={() => setShowHeightModal(false)}
        onSuccess={handleHeightSuccess}
        onError={onError}
      />
    </div>
  );
};

export default BodyMeasurementModal;
